import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../api';
import { applyTheme } from '../utils/themes';

const FEATURES = [
  {
    icon: '🕐',
    title: 'Clock-In Tracking',
    desc: 'Real-time shift management with instant clock-in panels posted directly in your Discord channels',
  },
  {
    icon: '💵',
    title: 'Payroll Dashboard',
    desc: 'Track earnings, outstanding balances, and process payments for individual staff or everyone at once',
  },
  {
    icon: '🏆',
    title: 'Rank System',
    desc: 'Configurable promotion tiers with custom hourly rates, required hours, and Discord role integration',
  },
  {
    icon: '🎁',
    title: 'Bonus Engine',
    desc: 'Bulk bonuses, holiday events, scheduled payouts, and percentage-based rewards — all from the dashboard',
  },
  {
    icon: '📊',
    title: 'Live Analytics',
    desc: 'Period hours, lifetime earnings, top performers, and budget summaries updated in real time',
  },
  {
    icon: '📋',
    title: 'Audit Logs',
    desc: 'Every clock-in, payment, bonus, and staff change is logged with full detail and searchable history',
  },
  {
    icon: '📅',
    title: 'Scheduled Reports',
    desc: 'Automatic weekly summaries posted to any Discord channel — staff count, hours, pay totals, top earners',
  },
  {
    icon: '📥',
    title: 'CSV Export',
    desc: 'Download payroll and shift data instantly to Excel with one click — no backend required',
  },
  {
    icon: '🎨',
    title: '32 Themes',
    desc: 'Choose from 32 hand-crafted color themes. Set a server-wide default or let each admin pick their own',
  },
  {
    icon: '🖼️',
    title: 'Custom Backgrounds',
    desc: 'Paste any image or GIF URL as your dashboard background with an adjustable opacity overlay',
  },
  {
    icon: '👥',
    title: 'Dashboard Admins',
    desc: 'Grant other Discord members access to manage your dashboard with configurable permission levels',
  },

];

const STATS = [
  { value: '32', label: 'Themes' },
  { value: '∞', label: 'Staff' },
  { value: '100%', label: 'Discord Native' },
];

export default function LoginPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    applyTheme('brazyy_pink');
  }, []);

  useEffect(() => {
    if (!loading && user) navigate('/servers');
  }, [user, loading, navigate]);

  const handleLogin = () => {
    window.location.href = authAPI.getLoginUrl();
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--gradient-hero)',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'var(--font-body)',
    }}>
      {/* Decorative blobs */}
      <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
        <div style={{
          position: 'absolute', top: '-10%', right: '-5%',
          width: '500px', height: '500px', borderRadius: '50%',
          background: 'var(--accent-soft)', opacity: 0.4, filter: 'blur(60px)'
        }} />
        <div style={{
          position: 'absolute', bottom: '-10%', left: '-5%',
          width: '400px', height: '400px', borderRadius: '50%',
          background: 'var(--accent-soft)', opacity: 0.3, filter: 'blur(80px)'
        }} />
        <div style={{
          position: 'absolute', top: '40%', left: '30%',
          width: '300px', height: '300px', borderRadius: '50%',
          background: 'var(--accent-secondary)', opacity: 0.08, filter: 'blur(80px)'
        }} />
      </div>

      {/* Nav */}
      <nav style={{
        position: 'relative', zIndex: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 40px',
        borderBottom: '1px solid var(--border)',
        background: 'rgba(255,255,255,0.6)',
        backdropFilter: 'blur(12px)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '50%',
            background: 'var(--gradient-accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '20px'
          }}>🕐</div>
          <span style={{
            fontFamily: 'var(--font-display)',
            fontSize: '22px', fontWeight: 700,
            color: 'var(--accent-primary)',
          }}>Brazyy's Clock-In</span>
        </div>
        <button onClick={handleLogin} style={{
          background: 'var(--gradient-accent)',
          color: '#fff', border: 'none', borderRadius: '10px',
          padding: '10px 24px', fontFamily: 'var(--font-body)',
          fontSize: '14px', fontWeight: 700, cursor: 'pointer',
          boxShadow: 'var(--shadow)',
        }}>Login with Discord</button>
      </nav>

      {/* Hero */}
      <div style={{
        position: 'relative', zIndex: 10,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '80px 40px 60px', textAlign: 'center',
      }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '8px',
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: '100px', padding: '6px 16px',
          fontSize: '13px', color: 'var(--text-secondary)',
          marginBottom: '28px', boxShadow: 'var(--shadow-card)',
        }}>
          <span style={{ color: 'var(--accent-primary)' }}>✦</span>
          Discord HR & Payroll Management
          <span style={{ color: 'var(--accent-primary)' }}>✦</span>
        </div>

        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'clamp(42px, 7vw, 80px)',
          fontWeight: 900, lineHeight: 1.05, marginBottom: '24px',
          background: 'var(--gradient-accent)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
        }}>Brazyy's Clock-In</h1>

        <p style={{
          fontSize: '18px', color: 'var(--text-secondary)',
          maxWidth: '560px', lineHeight: 1.7, marginBottom: '40px',
        }}>
          The complete HR management dashboard for Discord servers.
          Track shifts, manage payroll, and run your staff like a pro.
        </p>

        <button onClick={handleLogin} style={{
          background: 'var(--gradient-accent)',
          color: '#fff', border: 'none', borderRadius: '16px',
          padding: '18px 48px', fontFamily: 'var(--font-display)',
          fontSize: '20px', fontWeight: 700, cursor: 'pointer',
          boxShadow: '0 8px 32px rgba(232,52,28,0.30)',
          display: 'flex', alignItems: 'center', gap: '12px',
          marginBottom: '16px', transition: 'transform 0.2s, box-shadow 0.2s',
        }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(232,52,28,0.40)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(232,52,28,0.30)'; }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057.1 18.08.11 18.103.129 18.117a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
          </svg>
          Login with Discord
        </button>

        <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '60px' }}>
          Only servers where you have admin permissions will be shown
        </p>

        {/* Stats bar */}
        <div style={{
          display: 'flex', gap: '48px', justifyContent: 'center',
          marginBottom: '80px', flexWrap: 'wrap',
        }}>
          {STATS.map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{
                fontFamily: 'var(--font-display)', fontSize: '36px', fontWeight: 900,
                background: 'var(--gradient-accent)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                lineHeight: 1,
              }}>{s.value}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px', fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Section label */}
        <div style={{
          fontSize: '12px', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '2px', color: 'var(--text-muted)',
          marginBottom: '24px',
        }}>Everything included</div>

        {/* Feature grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: '16px', maxWidth: '1000px', width: '100%',
        }}>
          {FEATURES.map(f => (
            <div key={f.title} style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: '16px', padding: '22px', textAlign: 'left',
              boxShadow: 'var(--shadow-card)', transition: 'transform 0.2s, box-shadow 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = 'var(--shadow)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'var(--shadow-card)'; }}
            >
              <div style={{ fontSize: '28px', marginBottom: '10px' }}>{f.icon}</div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px' }}>{f.title}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <footer style={{
        position: 'relative', zIndex: 10,
        textAlign: 'center', padding: '24px',
        borderTop: '1px solid var(--border)',
        fontSize: '13px', color: 'var(--text-muted)',
        background: 'rgba(255,255,255,0.4)',
        backdropFilter: 'blur(8px)',
      }}>
        © 2026 Brazyy's Clock-In — Built by Brazyy
      </footer>
    </div>
  );
}
